import { some } from "lodash"
import { nanoid } from "nanoid"

import type { Design, Panel } from "."
import type { Point2d, Scale2d } from "../2d"
import {
  nameShape,
  getInsetAreas,
  findMinMaxPoints,
  rotatePoint,
  pointsToBox,
  boxToMatrix,
  doesPolygon,
  isPointInPolygon,
} from "../2d"
import { MAX_AUTO_PLACED_PANELS } from "../constants"
import { matchAdjacentPairs } from "../matching"
import type { Face2dComputed, Edge2d, Edge2dComputed } from "../mesh"

type PlacePanelsOptions = {
  design: Pick<Design, "edges" | "installAreas" | "nodes" | "setbacks"> & {
    solarModule?: { id: number } | null
  }
  moduleScale: Scale2d
  areaId: string
  inchesToPixels: (inches: number) => number
  numberOfPanels: number
}

type PlacePanelsPayload = {
  panels: Record<string, Panel>
  procedure: { nodes: Point2d[][]; faces: Face2dComputed[] }
}

export const placePanels = ({
  design,
  moduleScale,
  areaId,
  inchesToPixels,
  numberOfPanels,
}: PlacePanelsOptions): PlacePanelsPayload => {
  const installArea = design.installAreas[areaId]
  if (!installArea) {
    throw new Error(`Area not found`)
  }
  const { azimuth } = installArea
  if (azimuth === null || azimuth === undefined) {
    throw new Error(`Azimuth not found`)
  }

  const { h, w } = moduleScale

  const nodes = installArea.nodeIds.map((nodeId) => design.nodes[nodeId])

  const edges = matchAdjacentPairs(installArea.nodeIds, (p1Id, p2Id) => {
    const edge = design.edges[nameShape(p1Id, p2Id)]

    return {
      ...edge,
      p1: design.nodes[p1Id],
      p2: design.nodes[p2Id],
      setback: inchesToPixels(design.setbacks[edge.edgeType]),
    }
    // edges supplied to getInsetAreas is breaking the gateway
    // tsc step, for some reason it things that edges.nodexId
    // is optional, but it is not.  Logic and Client compilers do not agree
    // with the gateway compiler and have no issue here
  }) as (Edge2d & Edge2dComputed & { setback: number })[]

  const insetFaces = getInsetAreas({ areaId, area: { nodes }, edges }).result

  const faces: Face2dComputed[] = insetFaces[0] ? insetFaces : [{ nodes }]
  const allNodes = faces.flatMap((face) => face.nodes)

  const origin = findMinMaxPoints(...allNodes).min

  const straightenedFaces: Face2dComputed[] = faces.map((face) => ({
    nodes: face.nodes.map((node) => ({
      ...node,
      ...rotatePoint(origin, node, -azimuth),
    })),
  }))
  const straightenedNodes = allNodes.map((node) => ({
    ...node,
    ...rotatePoint(origin, node, -azimuth),
  }))

  const min: Point2d = { x: Infinity, y: Infinity }
  const max: Point2d = { x: 0, y: 0 }

  straightenedNodes.forEach((node) => {
    min.x = Math.min(min.x, node.x)
    min.y = Math.min(min.y, node.y)
    max.x = Math.max(max.x, node.x)
    max.y = Math.max(max.y, node.y)
  })

  const baseline = {
    p1: { x: min.x, y: min.y },
    p2: { x: max.x, y: min.y },
  }

  const box = pointsToBox(min, max)

  const matrix = boxToMatrix(box, { x: w, y: h }, 1, { x: 1, y: 1 })

  const panelPoints = matrix.map(({ x, y }) => [
    { x, y },
    { x: x + w, y },
    { x, y: y + h },
    { x: x + w, y: y + h },
  ])

  const otherAreas: [string, Face2dComputed][] = Object.entries(
    design.installAreas,
  ).map(([id, area]) => [
    id,
    {
      nodes: area.nodeIds
        .map((nodeId) => design.nodes[nodeId])
        .map((node) => rotatePoint(origin, node, -azimuth)),
    },
  ])
  const invalidAreas = otherAreas
    .filter(
      ([otherAreaId, otherArea]) =>
        otherAreaId !== areaId &&
        straightenedFaces.some((straightenedFace) =>
          doesPolygon(straightenedFace).containPolygon(otherArea),
        ),
    )
    .map(([, area]) => area)

  const isInsideInvalidArea = (point: Point2d) =>
    invalidAreas.some((area) => doesPolygon(area).containPoint(point))

  let validPanelOrigins = panelPoints
    .filter(
      (points) =>
        points.every((point) =>
          straightenedFaces.some((straightenedFace) =>
            isPointInPolygon(point, straightenedFace),
          ),
        ) && !points.some(isInsideInvalidArea),
    )
    .map((points) => points[0])

  if (numberOfPanels + validPanelOrigins.length > MAX_AUTO_PLACED_PANELS) {
    validPanelOrigins = validPanelOrigins.slice(
      0,
      MAX_AUTO_PLACED_PANELS - numberOfPanels,
    )
  }

  const reorientedPanelPoints = validPanelOrigins.map((point) => {
    const { x, y } = rotatePoint(origin, point, azimuth)

    return { x, y }
  })

  const panels = reorientedPanelPoints.reduce<Record<string, Panel>>(
    (acc, { x, y }) => {
      acc[nanoid()] = {
        x,
        y,
        w,
        h,
        r: azimuth,
        isActive: true,
        xSnap: null,
        ySnap: null,
        orientation: 0,
        wOriginal: w,
        hOriginal: h,
        module:
          design.solarModule?.id != null ? String(design.solarModule.id) : null,
        moduleOsId: design.solarModule?.id ?? null,
      }

      return acc
    },
    {},
  )
  const procedure: PlacePanelsPayload[`procedure`] = {
    nodes: [
      [baseline.p1, baseline.p2],
      [min, max],
      matrix,
      validPanelOrigins,
      reorientedPanelPoints,
    ],
    faces: [{ nodes: allNodes }, { nodes: straightenedNodes }],
  }

  return { panels, procedure }
}
